import React, { createContext, forwardRef, useContext, useState } from "react";
import { T } from "../../app/tokens.js";
import { getColHeaderHighlight } from "../utils/sortColumnHighlight.js";

const SortContext = createContext(null);

// Effective sorting owns presentation; preference/activity flags do not.
// Polymorphic roots retain the collection's existing table or grid geometry.
export const CollectionSortHeader = forwardRef(function CollectionSortHeader({
  as: Root = "th", active = false, direction = 1, sortable = true, standard = true,
  style, children, onClick, onKeyDown, onMouseEnter, onMouseLeave, ...props
}, ref) {
  const [hovered, setHovered] = useState(false);
  const dir = direction === "asc" || direction === 1 ? 1 : -1;
  const keyboard = Root !== "button" && sortable;
  const ariaSort = props["aria-sort"] ?? (sortable ? active ? dir > 0 ? "ascending" : "descending" : "none" : undefined);
  const element = <Root {...props} ref={ref}
      {...(Root === "button" ? { type: "button" } : {})}
      role={Root === "div" || (standard && Root === "th") ? "columnheader" : props.role}
      scope={standard && Root === "th" ? "col" : props.scope}
      aria-sort={Root === "button" ? undefined : ariaSort}
      tabIndex={keyboard ? 0 : props.tabIndex}
      onClick={onClick}
      onKeyDown={onKeyDown || (keyboard ? event => {
        if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onClick?.(event);
        }
      } : undefined)}
      onMouseEnter={event => { setHovered(true); onMouseEnter?.(event); }}
      onMouseLeave={event => { setHovered(false); onMouseLeave?.(event); }}
      style={{ ...style, ...(standard && active && sortable ? getColHeaderHighlight("active", "active", true) : {}),
        ...(standard && active && sortable ? { borderBottom: "none" } : {}) }}>
      {children}
    </Root>;
  return <SortContext.Provider value={{ active, dir, hovered, standard }}>
    {Root === "button"
      ? <div role="columnheader" aria-sort={ariaSort} style={{ display: "contents" }}>{element}</div>
      : element}
  </SortContext.Provider>;
});

export function CollectionSortCaret({ testId, style }) {
  const { active, dir, hovered, standard } = useContext(SortContext);
  return <svg aria-hidden="true" data-testid={testId} width="11" height="11" viewBox="0 0 24 24" fill="none"
    stroke={active ? T.timelineBlue : T.mid} strokeWidth={active ? 3 : 2.5}
    strokeLinecap="round" strokeLinejoin="round"
    style={{ marginLeft: "3px", verticalAlign: "-2px", flexShrink: 0, ...style, opacity: active ? standard ? 1 : undefined : hovered || !standard ? 0.25 : 0 }}>
    <polyline points={active && dir < 0 ? "6 9 12 15 18 9" : "18 15 12 9 6 15"} />
    {!standard && !active && <polyline points="6 9 12 15 18 9" />}
  </svg>;
}
